import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Loader2, Eye, SkipForward, Feather } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const src = (u) => (u?.startsWith("http") ? u : `${BACKEND}${u}`);
const cap = (s = "") => s.charAt(0).toUpperCase() + s.slice(1);
const BIG = "h-16 px-7 rounded-2xl font-patient font-black text-xl inline-flex items-center gap-2 transition-colors active:scale-95";

export const GROUP_WHOSAID = { id: "fable_whosaid", type: "fable_whosaid", title: "Qui a dit quoi ?", description: "" };

// Version groupe : l'animateur lit la réplique, les résidents devinent, puis il révèle l'animal.
export function GroupWhoSaid({ onEnd }) {
  const [rounds, setRounds] = useState(null);
  const [n, setN] = useState(0);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    let ignore = false;
    api.get("/fables/quiz", { params: { n: 10 } }).then((r) => !ignore && setRounds(r.data)).catch(() => !ignore && setRounds([]));
    return () => { ignore = true; };
  }, []);
  useEffect(() => { rounds?.[n + 1]?.options.forEach((o) => { new Image().src = src(o.image); }); }, [rounds, n]);
  if (!rounds) return <Loader2 className="w-8 h-8 animate-spin text-emerald-700 mx-auto" />;
  const r = rounds[n];
  if (!r) return <p className="text-xl font-patient text-gray-500 text-center py-10" data-testid="gws-empty">Publiez des fables pour jouer.</p>;
  const ans = r.options.find((o) => o.key === r.answer);
  const next = () => (n + 1 < rounds.length ? (setN(n + 1), setShown(false)) : onEnd());
  return (
    <div className="rounded-3xl bg-white border-4 border-amber-100 p-8 text-center" data-testid="gws-round">
      <p className="font-patient text-xl text-gray-500 mb-4" data-testid="gws-progress">Réplique {n + 1} sur {rounds.length}</p>
      <blockquote className="mx-auto max-w-3xl font-serif-display italic text-3xl sm:text-5xl leading-snug text-amber-950 mb-8" data-testid="gws-quote">« {r.text} »</blockquote>
      {shown ? (
        <div className="mb-8 soft-in" data-testid="gws-answer">
          <img src={src(ans?.image)} alt={ans?.name} className="mx-auto w-72 max-w-full aspect-[4/3] object-cover rounded-3xl border-4 border-amber-200" />
          <p className="font-patient font-black text-4xl text-gray-800 mt-4">{cap(ans?.name)}</p>
          <p className="font-patient text-2xl text-gray-500 mt-1">« {r.fable} »</p>
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-4 max-w-3xl mx-auto mb-8" data-testid="gws-options">
          {r.options.map((o) => (
            <div key={o.key} className="rounded-3xl border-4 border-gray-100 p-3">
              <img src={src(o.image)} alt={o.name} className="w-full aspect-[4/3] object-cover rounded-2xl" />
              <p className="mt-2 font-patient font-black text-2xl text-gray-800">{cap(o.name)}</p>
            </div>
          ))}
        </div>
      )}
      <div className="flex flex-wrap justify-center gap-4">
        {!shown ? (
          <button onClick={() => setShown(true)} data-testid="gws-reveal" className={`${BIG} bg-amber-400 hover:bg-amber-300 text-amber-950`}><Eye className="w-6 h-6" /> Révéler qui l'a dit</button>
        ) : (
          <button onClick={next} data-testid="gws-next" className={`${BIG} bg-emerald-700 hover:bg-emerald-800 text-white`}><SkipForward className="w-6 h-6" /> {n + 1 < rounds.length ? "Réplique suivante" : "Terminer"}</button>
        )}
      </div>
    </div>
  );
}

export function WhoSaidCard({ onPick }) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let ignore = false;
    api.get("/fables/quiz", { params: { n: 10 } }).then((r) => !ignore && setCount(r.data.length)).catch(() => {});
    return () => { ignore = true; };
  }, []);
  if (!count) return null;
  return (
    <button onClick={() => onPick(GROUP_WHOSAID)} data-testid="gg-whosaid-card"
      className="w-full text-left rounded-3xl bg-amber-50 border-4 border-amber-200 hover:border-amber-300 p-5 transition-colors flex items-center gap-4">
      <span className="w-14 h-14 rounded-2xl bg-amber-500 flex items-center justify-center shrink-0"><Feather className="w-7 h-7 text-white" /></span>
      <span>
        <span className="block font-serif-display text-2xl text-amber-900">Qui a dit quoi ?</span>
        <span className="block text-amber-800">{count} répliques des fables de La Fontaine : les résidents retrouvent l'animal, vous révélez la réponse.</span>
      </span>
    </button>
  );
}
